import { CircleAlert, CircleCheck, CircleQuestionMark, CircleSlash, CircleX, Loader, LucideIcon } from 'lucide-react'
import { match, P } from 'ts-pattern'
import { FileFormatIcons } from '~/components/FileFormatIcons'
import { Badge, Icon } from '~/elements'
import { CrawlTaskProgress } from '~/features/bots/stores/useCrawlerStore'
import { Color } from '~/types/colors'
import { cn } from '~/utils'
import { KnowledgeSourceStatusType, KnowledgeSourceType } from '../../types'

const { pdfIcon, docxIcon, docIcon, htmlIcon, mdIcon, typeIcon, urlIcon, txtIcon } = FileFormatIcons

export const KnowledgeSourceIcon = ({
  dsType,
  contentType,
  size = '5',
}: {
  dsType: KnowledgeSourceType
  contentType?: string
  size?: React.ComponentProps<typeof Icon>['size']
}) => {
  let resultIcon = undefined
  if (dsType === 'rich-text') {
    resultIcon = typeIcon
  } else if (dsType === 'web-page') {
    resultIcon = htmlIcon
  } else if (contentType) {
    resultIcon = match(contentType)
      .with('application/pdf', () => pdfIcon)
      .with('docx', () => docxIcon)
      .with('application/vnd.openxmlformats-officedocument.wordprocessingml.document', () => docIcon)
      .with(P.string.startsWith('text/html'), () => htmlIcon)
      .with('text/markdown', () => mdIcon)
      .with('text/plain', () => txtIcon)
      .otherwise(() => undefined)
  }

  if (!resultIcon) {
    resultIcon = match<KnowledgeSourceType, React.FC<React.SVGProps<SVGSVGElement>>>(dsType)
      .with('document', () => pdfIcon)
      .with('rich-text', () => typeIcon)
      .with('web-page', () => urlIcon)
      .otherwise(() => txtIcon)
  }

  return <Icon icon={resultIcon} size={size} />
}

export const getKnowledgeSourceOrigin = (dsType: KnowledgeSourceType) => {
  return match(dsType)
    .with('document', () => 'Manually Uploaded')
    .with('rich-text', () => 'Inline Document')
    .with('web-page', () => 'Web Sync')
    .otherwise(() => 'Manually Uploaded')
}

export const KnowledgeSourceOrigin = ({ dsType }: { dsType: KnowledgeSourceType }) => {
  const text = getKnowledgeSourceOrigin(dsType)

  return (
    <Badge color="gray" variant="outline" className="whitespace-nowrap text-gray-10">
      {text}
    </Badge>
  )
}

export const getKnowledgeSourceStatus = (status: KnowledgeSourceStatusType) => {
  type MatchOutput = {
    color: Color
    text: string
    icon: LucideIcon
  }

  return match<KnowledgeSourceStatusType, MatchOutput>(status)
    .with('upload_completed', () => ({ color: 'green', text: 'Uploaded', icon: CircleCheck }))
    .with('upload_failed', () => ({ color: 'red', text: 'Failed', icon: CircleX }))
    .with('upload_pending', () => ({ color: 'amber', text: 'Uploading', icon: Loader }))
    .with('indexing_pending', () => ({ color: 'plum', text: 'Indexing', icon: Loader }))
    .with('indexing_completed', () => ({ color: 'green', text: 'Indexed', icon: CircleCheck }))
    .with('indexing_failed', () => ({ color: 'red', text: 'Indexing failed', icon: CircleX }))
    .otherwise(() => ({ color: 'gray', text: 'Unknown', icon: CircleQuestionMark }))
}

export const KnowledgeSourceStatus = ({ status }: { status: KnowledgeSourceStatusType }) => {
  const { color, text, icon: Icon } = getKnowledgeSourceStatus(status)

  return (
    <div className={`flex items-center justify-start gap-4 text-${color}-8`}>
      <Icon
        size={16}
        className={cn(`stroke-${color}-8`, status.includes('pending') && 'animate-[spin_2s_linear_infinite]')}
      />
      <p className="text-sm font-medium">{text}</p>
    </div>
  )
}

export const KnowledgeSourceCrawlStatus = ({ status }: { status: CrawlTaskProgress['status'] }) => {
  type MatchOutput = {
    color: Color
    text: string
    icon: LucideIcon
  }

  const { color, text, ...rest } = match<CrawlTaskProgress['status'], MatchOutput>(status)
    .with('pending', () => ({ color: 'amber', text: 'Crawling pending', icon: Loader }))
    .with('in_progress', () => ({ color: 'plum', text: 'Crawling', icon: Loader }))
    .with('failed', () => ({ color: 'red', text: 'Crawling Failed', icon: CircleX }))
    .with('completed', () => ({ color: 'green', text: 'Crawling Completed', icon: CircleCheck }))
    .with('cancelled', () => ({ color: 'gray', text: 'Crawling Cancelled', icon: CircleSlash }))
    .with('timedout', () => ({ color: 'red', text: 'Crawling Timed out', icon: CircleAlert }))
    .otherwise(() => ({ color: 'gray', text: 'Crawling Unknown', icon: CircleQuestionMark }))

  return (
    <div className={`flex items-center justify-start gap-4 text-${color}-8`}>
      <rest.icon
        size={16}
        className={cn(`stroke-${color}-8`, rest.icon === Loader && 'animate-[spin_2s_linear_infinite]')}
      />
      <p className="text-sm font-medium">{text}</p>
    </div>
  )
}
